import "@/App.css";
import { useEffect, useRef } from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import LandingPage from "@/pages/LandingPage";
import ExpertisePage from "@/pages/ExpertisePage";
import CityPage from "@/pages/CityPage";
import ThankYouPage from "@/pages/ThankYouPage";
import { EXPERTISE_PAGES_ENABLED } from "@/lib/flags";

const useLenis = () => {
    const lenisRef = useRef(null);
    useEffect(() => {
        const lenis = new Lenis({
            duration: 1.1,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
        });
        lenisRef.current = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            lenisRef.current = null;
        };
    }, []);
    return lenisRef;
};

const ScrollToTop = ({ lenisRef }) => {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const scrollToHash = () => {
                const el = document.querySelector(hash);
                if (el) {
                    if (lenisRef.current) lenisRef.current.scrollTo(el);
                    else el.scrollIntoView({ behavior: "smooth" });
                }
            };
            const t = setTimeout(scrollToHash, 120);
            return () => clearTimeout(t);
        }
        if (lenisRef.current) {
            lenisRef.current.scrollTo(0, { immediate: true });
        } else {
            window.scrollTo(0, 0);
        }
    }, [pathname, hash, lenisRef]);
    return null;
};

const AppRoutes = () => {
    const lenisRef = useLenis();
    return (
        <>
            <ScrollToTop lenisRef={lenisRef} />
            <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/merci" element={<ThankYouPage />} />
                <Route
                    path="/expertise/:slug"
                    element={
                        EXPERTISE_PAGES_ENABLED ? (
                            <ExpertisePage />
                        ) : (
                            <Navigate to="/" replace />
                        )
                    }
                />
                <Route
                    path="/personal-branding-geneve"
                    element={<CityPage slugProp="personal-branding-geneve" />}
                />
                <Route
                    path="/personal-branding-lausanne"
                    element={
                        <CityPage slugProp="personal-branding-lausanne" />
                    }
                />
            </Routes>
        </>
    );
};

function App() {
    return (
        <HelmetProvider>
            <BrowserRouter>
                <AppRoutes />
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
